import React, {useEffect, useState} from 'react';
import {
    CBadge,
    CButton,
    CCardBody,
    CCardHeader,
    CCol,
    CCollapse,
    CFormGroup,
    CInput,
    CLabel,
    CRow,
    CSelect,
    CSwitch,
    CTextarea
} from '@coreui/react';
import {
    CampoFormData,
    ChecklistItemFormData,
    ChecklistSecaoFormData,
    OpcoesFormData,
    TIPOS_COM_OPCOES,
    TipoCampoList,
    TipoCampoTypes,
    TipoMetricaList,
    TipoPerguntaList,
    TipoPerguntaTypes
} from "./index";
import CIcon from "@coreui/icons-react";
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd";
import Form, {FormApi} from "../../../components/form";
import useCurrentAction from "../../../services/useCurrentAction";
import useCustomForm from '../../../services/useCustomForm'
import {useParams} from "react-router-dom";
import {getApi} from "../../../apis/backendApis";
import useLoading from "../../../services/useLoading";
import useAlert from "../../../services/useAlert";
import Select from "react-select";

const ChecklistItemForm = ()=>{

    interface GetParam {
        id?: string
    }

    const [formApi, setFormApi] = useState<FormApi>();
    const action = useCurrentAction();
    const { id } = useParams<GetParam>();
    const [accordion, setAccordion] = useState<number | null>(0);
    const [trashHover, setTrashHover] = useState<number | null>(null);
    const [plusHover, setPlusHover] = useState<number | null>(null);
    const [editorContent, setEditorContent] = useState<string[]>(['']);
    const [itemCondicional, setItemCondicional] = useState(false);
    const [setLoading] = useLoading();
    const [showAlert] = useAlert();

    // --- Seções (refs #479035) ---------------------------------------------------------------
    // As seções vivem em estado próprio, fora do `values` do useCustomForm, porque o hook trabalha
    // com uma lista só. O vínculo item -> seção é feito pelo `draggId` da seção enquanto estamos no
    // client; só na hora de enviar ele vira o índice da seção no array, que é o que o backend
    // (ChecklistFormService::resolveSecaoDosItens) sabe resolver tanto para seção nova quanto para
    // seção já gravada.
    const SEM_SECAO = 'sem-secao';
    const [secoes, setSecoes] = useState<Array<ChecklistSecaoFormData>>([]);

    const getInitialValueSecao = ():ChecklistSecaoFormData => ({
        nome: '',
        draggId: Math.random().toString(),
    });

    // null = o grupo dos itens que não pertencem a nenhuma seção.
    const getDroppableIdPorDraggId = (draggIdSecao:string | null) => (
        draggIdSecao === null ? SEM_SECAO : `itens-${draggIdSecao}`
    );

    const getDroppableIdSecao = (secao:ChecklistSecaoFormData) => getDroppableIdPorDraggId(secao.draggId);

    const getSecaoPorDroppableId = (droppableId:string) => (
        secoes.find(secao => getDroppableIdSecao(secao) === droppableId)
    );

    // Índices de `values` pertencentes a um grupo, na ordem em que aparecem. Preservar o índice
    // original é o que permite continuar usando handleChange(event, index) do useCustomForm.
    const getIndicesDoGrupo = (draggIdSecao:string | null) => {
        const indices:number[] = [];
        values.forEach((item:ChecklistItemFormData, index:number) => {
            const secaoDoItem = item.secao ?? null;
            if (secaoDoItem === draggIdSecao) {
                indices.push(index);
            }
        });
        return indices;
    };

    // O CInput do CoreUI tipa o onChange como FormEvent, então o alvo é estreitado aqui.
    const handleChangeSecao = (event:React.FormEvent<any>, indexSecao:number)=>{
        const nome = (event.target as HTMLInputElement).value;
        const novas = [...secoes];
        novas.splice(indexSecao, 1, {...novas[indexSecao], nome: nome});
        setSecoes(novas);
    };

    const handlePlusSecao = ()=>{
        setSecoes([...secoes, getInitialValueSecao()]);
    };

    const handleMinusSecao = (indexSecao:number)=>{
        const removida = secoes[indexSecao];
        const novas = [...secoes];
        novas.splice(indexSecao, 1);

        const novosValues = values.map((item:ChecklistItemFormData)=>(
            item.secao === removida.draggId ? {...item, secao: null} : item
        ));

        setSecoes(novas);
        setValues(novosValues);
    };

    const getInitialValueOpcoes = (numero?:number)=>{
        return {
            numero: numero !== undefined ? numero : 1,
            descricao:''
        }
    }

    const getInitialValueCampo = (numero?:number):CampoFormData=>{
        return {
            numero: numero !== undefined ? numero : 1,
            label: '',
            tipoCampo: TipoCampoTypes.TEXTO,
            obrigatorio: false
        }
    }

    const getInitialValueItem = () => {
        return {
            itemCondicional: '',
            draggId: Math.random().toString(),
            tipo: TipoPerguntaTypes.OBJETIVA,
            descricao: '',
            titulo: '',
            obrigatorio:false,
            tipoMetrica: undefined,
            opcoes: [
                getInitialValueOpcoes()
            ],
            config: [
                getInitialValueCampo()
            ]
        }
    }

    const initialValues:Array<ChecklistItemFormData> = [
        getInitialValueItem()
    ]

    const {
        values,
        setValues,
        handleChange,
        handleSubmit,
        handleSubmitValues,
        handleClickMinus,
        handleClickPlus,
    } = useCustomForm(

        {
            initialValues,
            route:'checklist',
            action:'itens',
            onSubmit: values => {
                let ordem = 1;
                let erro:{type:string, msg:string, title?:string} | undefined = undefined;
                const secoesValidas = secoes.filter(secao => secao.nome.trim() !== '');
                const itensNaOrdemDaTela = achataGrupos(agrupaItens(values.values, secoesValidas), secoesValidas);
                let itensOrdenados = itensNaOrdemDaTela.map((item:ChecklistItemFormData, index:number)=>{
                    let isPreenchido = (s:any[])=>{
                        let preenchidos = s.filter((val)=>{
                            if(val !== undefined){
                                let newS = val.toString().trim();
                                return newS !== '';
                            }
                        })
                        return preenchidos.length === s.length;
                    }

                    let exigeOpcoes = (tipo:number) => (TIPOS_COM_OPCOES.includes(tipo*1));

                    let opcoesPreenchidas = item.opcoes.filter((opt)=>(isPreenchido([opt.descricao])));

                    if(!isPreenchido([item.descricao, item.titulo])){
                        erro = {type:'danger', msg: `Necessário preencher os campos obrigatórios Título e Descrição. Item ${index+1}`, title: 'Alerta'};
                    }

                    if((exigeOpcoes(item.tipo) && opcoesPreenchidas.length <= 1)){
                        erro = {type:'danger', msg: `Necessária mais de uma opção para as questões Objetivas, de Múltipla Escolha e Checklist.  Item ${index+1}`, title: 'Alerta'};
                    }

                    let camposPreenchidos:CampoFormData[] = [];
                    if(item.tipo*1 === TipoPerguntaTypes.MULTIPLOS_CAMPOS){
                        camposPreenchidos = (item.config || []).filter((campo)=>(isPreenchido([campo.label])));

                        if(camposPreenchidos.length === 0){
                            erro = {type:'danger', msg: `Necessário informar ao menos um campo para o item de Múltiplos Campos. Item ${index+1}`, title: 'Alerta'};
                        } else if(item.obrigatorio && camposPreenchidos.filter((campo)=>campo.obrigatorio).length === 0){
                            erro = {type:'danger', msg: `Item de Múltiplos Campos obrigatório precisa de ao menos um campo marcado como obrigatório. Item ${index+1}`, title: 'Alerta'};
                        }
                    }

                    let tipoMetrica  = item.tipoMetrica && item.tipoMetrica*1;
                    tipoMetrica = tipoMetrica !== -1 ? tipoMetrica : undefined;


                    let config = item.tipo*1 === TipoPerguntaTypes.MULTIPLOS_CAMPOS ? camposPreenchidos : null;


                    const indiceSecao = secoesValidas.findIndex(secao => secao.draggId === item.secao);

                    return {
                        ...item,
                        ordem:ordem++,
                        tipoMetrica: tipoMetrica,
                        config: config,
                        secao: indiceSecao >= 0 ? indiceSecao : null,
                    }
                })
                itensOrdenados.map((item:ChecklistItemFormData, index:number)=>{
                    delete itensOrdenados[index].checkedCondicional
                });

                if(!erro && secoes.filter(secao => secao.nome.trim() === '').length > 0){
                    erro = {type:'danger', msg: 'Toda seção precisa de um nome. Preencha ou remova as seções em branco.', title: 'Alerta'};
                }

                if(erro){
                    let {msg} = erro;
                    showAlert({
                        msg: msg,
                        type: 'danger',
                        toaster: true,
                    });
                    return;
                } else {
                    handleSubmitValues({
                        secoes: secoesValidas.map(secao=>({id: secao.id, nome: secao.nome.trim()})),
                        itens: itensOrdenados,
                    });
                }
            },
        }
    );

    const editorButtons = [
        "undo",
        "redo",
        "font",
        "fontSize",
        "formatBlock",
        "paragraphStyle",
        "blockquote",
        "bold",
        "underline",
        "italic",
        "strike",
        "subscript",
        "superscript",
        "fontColor",
        "hiliteColor",
        "textStyle",
        "removeFormat",
        "outdent",
        "indent",
        "align",
        "horizontalRule",
        "list",
        "lineHeight",
        "table",
        "link"
    ];

    // useEffect(()=>{
    //     let index = accordion || 0;
    //     let actualValues = [...values];
    //     let newArrayValues = {...values[index], ['descricao']:editorContent && editorContent[index]}
    //     actualValues[index] = newArrayValues;
    //     setValues(actualValues);
    // },[editorContent])

    useEffect(()=>{
        if(id){
            let api = getApi();
            setLoading(true);

            api.get(`checklist/get_secoes/${id}`).then((responseSecoes)=>{
                const secoesCarregadas:Array<ChecklistSecaoFormData> = [...responseSecoes.data].map((secao:any)=>({
                    id: secao.id,
                    nome: secao.nome,
                    ordem: secao.ordem,
                    draggId: Math.random().toString(),
                }));
                setSecoes(secoesCarregadas);

                return api.get(`checklist/get_itens/${id}`).then((response)=>{
                    let newValues = [...response.data];
                    newValues = newValues.map((item:any, index) => {

                        if (newValues[index]?.itemCondicional?.id > 0){
                            newValues[index].checkedCondicional= true
                        }

                        const secaoDoItem = secoesCarregadas.find(secao => secao.id === item.secao);
                        return {
                            ...item,
                            draggId: Math.random().toString(),
                            secao: secaoDoItem ? secaoDoItem.draggId : null,
                            opcoes: item.opcoes && item.opcoes.length > 0 ? item.opcoes : [getInitialValueOpcoes()],
                            config: item.config && item.config.length > 0 ? item.config : [getInitialValueCampo()],
                        }
                    });
                    if(newValues.length > 0){
                        setValues(newValues);
                    }
                });
            }).finally(()=>{
                setLoading(false);
            });
        }

    },[id]);

    const handleFormReady = (formApi:FormApi)=>{
        setFormApi(formApi);
    }

    const handleChangeOpcoes = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | any>, indexItem: number, indexOpcao: number)=>{
        let actualValues:Array<ChecklistItemFormData> = [...values];
        if(actualValues[indexItem]){
            let item = actualValues[indexItem];
            let opcao = item.opcoes && {...item.opcoes[indexOpcao], descricao: event.target.value};

            if(opcao){
                actualValues[indexItem].opcoes.splice(indexOpcao,1, opcao);
            }

            setValues(actualValues);
        }
    }

    const getNextNumero= (opcoes:OpcoesFormData[])=>{
        let max = 0;
        opcoes.map(opcao=>{
            max = opcao.numero > max ? opcao.numero : max;
        })
        return max + 1;
    }

    const handleMinusOpcoes = (indexItem: number, indexOpcao: number)=>{
        let actualValues:Array<ChecklistItemFormData> = [...values];
        if(actualValues[indexItem]){
            if(actualValues[indexItem].opcoes.length > 1){
                actualValues[indexItem].opcoes.splice(indexOpcao,1);
            } else {
                actualValues[indexItem].opcoes.splice(indexOpcao,1, getInitialValueOpcoes());
            }

            setValues(actualValues);
        }
    }

    const handlePlusOpcoes = (indexItem:number)=>{
        let actualValues:Array<ChecklistItemFormData> = [...values];
        if(actualValues[indexItem]){
             let numero = getNextNumero(actualValues[indexItem].opcoes);
             actualValues[indexItem].opcoes.push(getInitialValueOpcoes(numero));

             setValues(actualValues);
        }
    }


    const getNextNumeroCampo = (campos:CampoFormData[])=>{
        let max = 0;
        campos.forEach(campo=>{
            max = campo.numero > max ? campo.numero : max;
        })
        return max + 1;
    }

    const handleChangeCampos = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | any>, indexItem: number, indexCampo: number)=>{
        let actualValues:Array<ChecklistItemFormData> = [...values];
        if(actualValues[indexItem]){
            let campos = actualValues[indexItem].config || [];
            let valor = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
            let campo = {...campos[indexCampo], [event.target.name]: valor};
            campos.splice(indexCampo, 1, campo);
            actualValues[indexItem].config = campos;

            setValues(actualValues);
        }
    }

    const handlePlusCampos = (indexItem:number)=>{
        let actualValues:Array<ChecklistItemFormData> = [...values];
        if(actualValues[indexItem]){
            let campos = actualValues[indexItem].config || [];
            campos.push(getInitialValueCampo(getNextNumeroCampo(campos)));
            actualValues[indexItem].config = campos;

            setValues(actualValues);
        }
    }

    const handleMinusCampos = (indexItem:number, indexCampo:number)=>{
        let actualValues:Array<ChecklistItemFormData> = [...values];
        if(actualValues[indexItem]){
            let campos = actualValues[indexItem].config || [];
            if(campos.length > 1){
                campos.splice(indexCampo,1);
            } else {
                campos.splice(indexCampo,1, getInitialValueCampo());
            }
            actualValues[indexItem].config = campos;

            setValues(actualValues);
        }
    }

    const isView = () => {
        return action === 'view';
    };


    const achataGrupos = (
        grupos:{[key:string]:Array<ChecklistItemFormData>},
        ordemSecoes:Array<ChecklistSecaoFormData>
    ):Array<ChecklistItemFormData> => {
        let planos:Array<ChecklistItemFormData> = [...(grupos[SEM_SECAO] || [])];
        ordemSecoes.forEach(secao=>{
            planos = planos.concat(grupos[getDroppableIdSecao(secao)] || []);
        });
        return planos;
    };

    const agrupaItens = (
        lista:Array<ChecklistItemFormData>,
        ordemSecoes:Array<ChecklistSecaoFormData>
    ) => {
        const grupos:{[key:string]:Array<ChecklistItemFormData>} = {[SEM_SECAO]: []};
        ordemSecoes.forEach(secao=>{
            grupos[getDroppableIdSecao(secao)] = [];
        });

        lista.forEach((item:ChecklistItemFormData)=>{
            const secao = ordemSecoes.find(s => s.draggId === item.secao);
            const chave = secao ? getDroppableIdSecao(secao) : SEM_SECAO;
            grupos[chave].push(item);
        });

        return grupos;
    };

    const onDragEnd = (result:DropResult) =>{
        const {source, destination, type} = result;

        if (!destination) {
            return;
        }


        if (type === 'SECAO') {
            if (destination.index === source.index) {
                return;
            }
            const novasSecoes = Array.from(secoes);
            const [removida] = novasSecoes.splice(source.index, 1);
            novasSecoes.splice(destination.index, 0, removida);

            setSecoes(novasSecoes);
            setValues(achataGrupos(agrupaItens(values, secoes), novasSecoes));
            return;
        }

        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const grupos = agrupaItens(values, secoes);
        const [movido] = grupos[source.droppableId].splice(source.index, 1);
        const secaoDestino = getSecaoPorDroppableId(destination.droppableId);

        grupos[destination.droppableId].splice(destination.index, 0, {
            ...movido,
            secao: secaoDestino ? secaoDestino.draggId : null,
        });

        setValues(achataGrupos(grupos, secoes));
    }

    const getTituloPergunta = (titulo:string): string =>{
        if(titulo != '' && titulo.length <= 30){
            return titulo;
        } else if(titulo != ''){
            return titulo.substring(0, 30)+'...';
        }

        return 'Informe um título';
    }

    const onChangeEditor = (newValue:string, index:number)=>{
        let actualValues = [...editorContent];
        actualValues.splice(index,1, newValue);
        setEditorContent(actualValues);
    }


    const handleClickPlusItem = (index:number) =>{
        const secaoDoVizinho = values[index]?.secao ?? null;
        handleClickPlus(()=>({...getInitialValueItem(), secao: secaoDoVizinho}), undefined, index);
        setAccordion(accordion === index+1 ? null : index+1)
    }


    const handleClickPlusNoGrupo = (draggIdSecao:string | null) =>{
        const grupos = agrupaItens(values, secoes);
        const chave = getDroppableIdPorDraggId(draggIdSecao);
        const novoItem = {...getInitialValueItem(), secao: draggIdSecao};

        grupos[chave] = [...(grupos[chave] || []), novoItem];
        const novosValues = achataGrupos(grupos, secoes);

        setValues(novosValues);
        setAccordion(novosValues.findIndex((item:ChecklistItemFormData) => item.draggId === novoItem.draggId));
    }

    const handleChangeSecaoDoItem = (event:React.FormEvent<any>, index:number)=>{
        const valor = (event.target as HTMLSelectElement).value;
        const novaSecao = valor === '' ? null : valor;
        const draggIdDoItem = values[index].draggId;

        const atualizados = values.map((item:ChecklistItemFormData, i:number)=>(
            i === index ? {...item, secao: novaSecao} : item
        ));
        const novosValues = achataGrupos(agrupaItens(atualizados, secoes), secoes);

        setValues(novosValues);
        // O reagrupamento muda os índices, então o accordion precisa seguir o item movido.
        setAccordion(novosValues.findIndex((item:ChecklistItemFormData) => item.draggId === draggIdDoItem));
    }

    const getListTitulos = (indexSelected: number) => {
        return values
            .filter((value: any, index: number) => value.id !== values[indexSelected].id || value.draggId !== values[indexSelected].draggId)
            .map((value: any, index: number) => ({
                label: value['titulo'].toString(),
                value: value.id > 0 ? value.id : index.toString()
            }));
    };

    const getListOpcoes = (index: number) => {
        const selectedCondicionalId = values[index]?.itemCondicional;
        let selectedChecklist = values.find((value: any) => value?.id === selectedCondicionalId);
        if (selectedChecklist == null){
            selectedChecklist = values[selectedCondicionalId]
        }if (selectedChecklist && selectedChecklist.opcoes) {
            if (selectedChecklist.tipo == 4){
                let data = [];
                data.push({
                    label: 'Sim',
                    value: 1
                });
                data.push({
                    label: 'Não',
                    value: 2
                });
                return data;
            }
            return selectedChecklist.opcoes.map((opcao: any) => ({
                label: opcao.descricao.toString(),
                value: opcao.numero
            }));
        }
    };

    const condicionais ={
        numero: '',
        descricao: ''
    }

    const handleIsCondicional = (condicao: boolean, index: number) =>{
        values[index].checkedCondicional = condicao;
        setItemCondicional(condicao)
        if (!values[index].checkedCondicional){
            values[index].valorCondicional = null
            values[index].itemCondicional = null
        }
    }

    const handleItemCondicional = (selectedOption: any, index: number) => {
        const updatedValues = [...values];
        updatedValues[index].itemCondicional = selectedOption.value;
        setValues(updatedValues);
    };

    const handleCondicionalOpcao = (selectedOption: any, index: number) => {
        const updatedValues = [...values];
        updatedValues[index].valorCondicional = condicionais
        updatedValues[index].valorCondicional.numero = selectedOption.value;
        updatedValues[index].valorCondicional.descricao = selectedOption.label;
        setValues(updatedValues);
    };



    const renderItem = (item:ChecklistItemFormData, index:number, posicao:number) => (
                                <Draggable
                                    draggableId={item.draggId}
                                    index={posicao}
                                    key={item.draggId}
                                    isDragDisabled={accordion === index}
                                >
                                    {provided => (
                                        <div
                                            key={item.draggId}
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className="card mb-2"
                                        >
                                            <CCardHeader className="py-0 pl-0">
                                                <CFormGroup className="row mb-0">
                                                    <div className="col-2">
                                                        <CButton
                                                            onClick={()=>handleClickMinus(index, getInitialValueItem)}
                                                            title="Remove o item da linha"
                                                            className={trashHover === index ? "border-danger" : ""}
                                                            onMouseOver={()=>{setTrashHover(index)}}
                                                            onMouseLeave={()=>{setTrashHover(null)}}
                                                        >
                                                            <CIcon name="cil-trash" className="c-icon" ></CIcon>
                                                        </CButton>
                                                        <CButton
                                                            onClick={()=>handleClickPlusItem(index)}
                                                            title="Adiciona um item"
                                                            className={plusHover === index ? "border-success" : ""}
                                                            onMouseOver={()=>{setPlusHover(index)}}
                                                            onMouseLeave={()=>{setPlusHover(null)}}
                                                        >
                                                            <CIcon name="cil-plus" className="c-icon" ></CIcon>
                                                        </CButton>
                                                    </div>
                                                    <CButton className="col-9" onClick={()=>{setAccordion(accordion === index ? null : index)}}>
                                                        <h5>
                                                            {getTituloPergunta(values[index].titulo)}
                                                        </h5>
                                                    </CButton>
                                                    <div className="col-1 row justify-content-end align-content-center pr-0">
                                                        <CIcon name="cil-apps" className="c-icon"></CIcon>
                                                        <CBadge title="Ordem" className="ml-2" shape="pill" color="secondary">{index+1}</CBadge>
                                                    </div>
                                                </CFormGroup>
                                            </CCardHeader>
                                            <CCollapse show={accordion === index}>
                                                <CCardBody>
                                                    <CFormGroup>
                                                        <CLabel htmlFor="titulo">Título</CLabel>
                                                        <CInput readOnly={isView()} disabled={isView()} type="text" id="titulo" name="titulo" onChange={event=>handleChange(event, index)}  value={values[index].titulo}/>
                                                    </CFormGroup>
                                                    <CFormGroup className="row">
                                                        <CCol className="col-4">
                                                            <CLabel htmlFor="tipo">Tipo</CLabel>
                                                            <CSelect readOnly={isView()} disabled={isView()} name="tipo" id="tipo"  onChange={event=>handleChange(event, index)} value={values[index].tipo}>
                                                                {TipoPerguntaList.getList().map(item=>(
                                                                    <option key={item.value} value={item.value}>{item.description}</option>
                                                                ))}
                                                            </CSelect>
                                                        </CCol>
                                                        <CCol className="col-4">
                                                            <CLabel htmlFor="tipoMetrica">Tipo de Métrica</CLabel>
                                                            <CSelect readOnly={isView()} disabled={isView()} name="tipoMetrica" id="tipoMetrica"  onChange={event=>handleChange(event, index)} value={values[index].tipoMetrica}>
                                                                {TipoMetricaList.getList(true).map(item=>(
                                                                    <option key={item.value} value={item.value}>{item.description}</option>
                                                                ))}
                                                            </CSelect>
                                                        </CCol>
                                                        <CCol className="col-4">
                                                            <CLabel className="offset-1 " htmlFor="obrigatorio" >Obrigatório</CLabel>
                                                            <CCol className="custom-switch">
                                                                <CSwitch color={'primary'} id="obrigatorio" name="obrigatorio"  checked={values[index].obrigatorio} onChange={event=>handleChange(event, index)}  readOnly={isView()} disabled={isView()}></CSwitch>
                                                            </CCol>
                                                        </CCol>
                                                    </CFormGroup>
                                                    {secoes.length > 0 && (
                                                        <CFormGroup>
                                                            <CLabel>Seção</CLabel>
                                                            <CSelect
                                                                readOnly={isView()}
                                                                disabled={isView()}
                                                                value={values[index].secao ?? ''}
                                                                onChange={event=>handleChangeSecaoDoItem(event, index)}
                                                            >
                                                                <option value="">Fora de seção</option>
                                                                {secoes.map((secao:ChecklistSecaoFormData)=>(
                                                                    <option key={secao.draggId} value={secao.draggId}>
                                                                        {secao.nome || '(seção sem nome)'}
                                                                    </option>
                                                                ))}
                                                            </CSelect>
                                                            <small className="form-text text-muted">
                                                                Alternativa ao arrastar: move o item para outra seção ou para fora de todas.
                                                            </small>
                                                        </CFormGroup>
                                                    )}
                                                    <CFormGroup>
                                                        <CLabel htmlFor="descricao">Descrição</CLabel>
                                                        <CTextarea rows={5} readOnly={isView()} disabled={isView()} id="descricao" name="descricao" onChange={event=>handleChange(event, index)}  value={values[index].descricao}></CTextarea>
                                                    </CFormGroup>
                                                    {TIPOS_COM_OPCOES.includes(values[index].tipo*1) && (
                                                        <CFormGroup>
                                                            <CLabel>
                                                                {values[index].tipo*1 === TipoPerguntaTypes.CHECKLIST ? 'Itens a validar' : 'Opções'}
                                                            </CLabel>
                                                            {values[index].opcoes && values[index].opcoes.map((opcao:OpcoesFormData, indexOP:number)=>(
                                                                <CRow key={indexOP}>
                                                                    <CCol className="col-10 d-flex align-items-center">
                                                                        <span className="pr-3">{`${opcao.numero}.`}</span>
                                                                        <CInput readOnly={isView()} disabled={isView()} type="text" id="descricao" name="descricao" onChange={event=>handleChangeOpcoes(event, index, indexOP)}  value={values[index].opcoes[indexOP].descricao}/>
                                                                    </CCol>
                                                                    <CCol className="col-2">
                                                                        <CButton onClick={()=>handlePlusOpcoes(index)}><CIcon name="cil-plus"></CIcon></CButton>
                                                                        <CButton onClick={()=>handleMinusOpcoes(index, indexOP)}><CIcon name="cil-trash"></CIcon></CButton>
                                                                    </CCol>
                                                                </CRow>
                                                            ))}
                                                        </CFormGroup>
                                                    )}
                                                    {values[index].tipo*1 === TipoPerguntaTypes.MULTIPLOS_CAMPOS && (
                                                        <CFormGroup>
                                                            <CLabel>Campos de preenchimento</CLabel>
                                                            <small className="form-text text-muted mb-2">
                                                                Cada campo é preenchido separadamente na execução, mas todos ficam agrupados neste único item.
                                                            </small>
                                                            {values[index].config && values[index].config.map((campo:CampoFormData, indexCP:number)=>(
                                                                <CRow key={indexCP} className="mb-1">
                                                                    <CCol className="col-1 d-flex align-items-center">
                                                                        <span>{`${campo.numero}.`}</span>
                                                                    </CCol>
                                                                    <CCol className="col-5">
                                                                        <CInput readOnly={isView()} disabled={isView()} type="text" name="label"
                                                                                placeholder="Rótulo do campo (ex.: Razão Social)"
                                                                                onChange={event=>handleChangeCampos(event, index, indexCP)}
                                                                                value={campo.label}/>
                                                                    </CCol>
                                                                    <CCol className="col-2">
                                                                        <CSelect readOnly={isView()} disabled={isView()} name="tipoCampo"
                                                                                 onChange={event=>handleChangeCampos(event, index, indexCP)}
                                                                                 value={campo.tipoCampo}>
                                                                            {TipoCampoList.map(opt=>(
                                                                                <option key={opt.value} value={opt.value}>{opt.description}</option>
                                                                            ))}
                                                                        </CSelect>
                                                                    </CCol>
                                                                    <CCol className="col-2 d-flex align-items-center custom-switch">
                                                                        <CSwitch color={'primary'} name="obrigatorio" title="Campo obrigatório"
                                                                                 checked={campo.obrigatorio}
                                                                                 onChange={event=>handleChangeCampos(event, index, indexCP)}
                                                                                 readOnly={isView()} disabled={isView()}></CSwitch>
                                                                        <span className="ml-2 small">Obrigatório</span>
                                                                    </CCol>
                                                                    <CCol className="col-2">
                                                                        <CButton onClick={()=>handlePlusCampos(index)}><CIcon name="cil-plus"></CIcon></CButton>
                                                                        <CButton onClick={()=>handleMinusCampos(index, indexCP)}><CIcon name="cil-trash"></CIcon></CButton>
                                                                    </CCol>
                                                                </CRow>
                                                            ))}
                                                        </CFormGroup>
                                                    )}
                                                    <CFormGroup className="row">
                                                        <CCol className="col">
                                                            <CLabel>Item Condicional?</CLabel>
                                                            <CCol className=" ">
                                                                <CSwitch
                                                                    color={'primary'}
                                                                    checked={values[index].checkedCondicional}
                                                                    onClick={() => {handleIsCondicional(!itemCondicional, index)}}
                                                                    readOnly={isView()}
                                                                    disabled={isView()}
                                                                ></CSwitch>
                                                            </CCol>
                                                        </CCol>
                                                        {values[index].checkedCondicional && (
                                                        <CCol className="col-10">
                                                            <CLabel>Condicional do Item: </CLabel>
                                                            <Select
                                                                options={getListTitulos(index)}
                                                                onChange={(selectedOption) => handleItemCondicional(selectedOption, index)}
                                                                value={values[index]?.itemCondicional ?
                                                                    getListTitulos(index).find((val: any) => val.value === values[index].itemCondicional.id) :
                                                                    undefined
                                                                }
                                                                noOptionsMessage={() => "Sem itens disponíveis"}
                                                            />
                                                        </CCol>
                                                        )}
                                                    </CFormGroup>
                                                    {values[index]?.itemCondicional != null && values[index].checkedCondicional &&
                                                        <CFormGroup>
                                                            <CLabel>Qual opção atende esse condicional: </CLabel>
                                                            <Select
                                                                options={getListOpcoes(index)}
                                                                onChange={(selectedOption)=>{handleCondicionalOpcao(selectedOption, index)}}
                                                                noOptionsMessage={() => "Sem opções disponíveis"}
                                                                value={values[index]?.valorCondicional ? {
                                                                       label: values[index]?.valorCondicional?.descricao,
                                                                       value: values[index]?.valorCondicional?.numero
                                                                }: undefined}
                                                            />
                                                        </CFormGroup>
                                                    }
                                                </CCardBody>
                                            </CCollapse>
                                        </div>
                                    )}
                                </Draggable>
    );


    const renderGrupoItens = (draggIdSecao:string | null, indices:number[]) => (
        <Droppable droppableId={getDroppableIdPorDraggId(draggIdSecao)} type="ITEM">
            {provided => (
                // minHeight garante área de soltura mesmo com o grupo vazio: sem isso não há
                // para onde arrastar um item de volta para fora da seção.
                <div ref={provided.innerRef} {...provided.droppableProps} className="pt-1" style={{minHeight: '2.5rem'}}>
                    {indices.map((index:number, posicao:number)=>(
                        renderItem(values[index], index, posicao)
                    ))}
                    {provided.placeholder}
                    {indices.length === 0 && (
                        <div className="d-flex align-items-center pl-2 pb-2">
                            <span className="text-muted small mr-2">Nenhum item aqui. Arraste um item ou</span>
                            <CButton
                                className="btn-outline-secondary btn-sm"
                                onClick={()=>handleClickPlusNoGrupo(draggIdSecao)}
                                disabled={isView()}
                                title="Adiciona um item neste grupo"
                            >
                                <CIcon name="cil-plus" className="c-icon mr-1"></CIcon> Adicionar item
                            </CButton>
                        </div>
                    )}
                </div>
            )}
        </Droppable>
    );

    const indicesSemSecao = getIndicesDoGrupo(null);

    return (
        <Form entity="Itens Checklist" handleSubmit={handleSubmit} onFormReady={handleFormReady}>
            <DragDropContext onDragEnd={onDragEnd} >
                {secoes.length > 0 && (
                    <h6 className="text-muted mt-2 mb-1">Itens fora de seção</h6>
                )}
                {renderGrupoItens(null, indicesSemSecao)}

                <Droppable droppableId="secoes" type="SECAO">
                    {provided => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {secoes.map((secao:ChecklistSecaoFormData, indexSecao:number)=>(
                                <Draggable
                                    draggableId={`secao-${secao.draggId}`}
                                    index={indexSecao}
                                    key={secao.draggId}
                                    isDragDisabled={isView()}
                                >
                                    {providedSecao => (
                                        <div
                                            ref={providedSecao.innerRef}
                                            {...providedSecao.draggableProps}
                                            className="card mb-3 border-primary"
                                        >
                                            <CCardHeader className="py-2 bg-light">
                                                <CFormGroup className="row mb-0 align-items-center">
                                                    <div className="col-1" {...providedSecao.dragHandleProps}
                                                         title="Arraste para reordenar a seção">
                                                        <CIcon name="cil-apps" className="c-icon"></CIcon>
                                                    </div>
                                                    <CCol className="col-9">
                                                        <CInput
                                                            readOnly={isView()}
                                                            disabled={isView()}
                                                            type="text"
                                                            placeholder="Nome da seção (ex.: Levantamento inicial)"
                                                            maxLength={150}
                                                            onChange={event=>handleChangeSecao(event, indexSecao)}
                                                            value={secao.nome}
                                                        />
                                                    </CCol>
                                                    <div className="col-2 text-right">
                                                        <CButton
                                                            onClick={()=>handleMinusSecao(indexSecao)}
                                                            title="Remove a seção. Os itens dela voltam para fora de qualquer seção."
                                                            disabled={isView()}
                                                        >
                                                            <CIcon name="cil-trash" className="c-icon"></CIcon>
                                                        </CButton>
                                                    </div>
                                                </CFormGroup>
                                            </CCardHeader>
                                            <CCardBody className="py-2">
                                                {renderGrupoItens(secao.draggId, getIndicesDoGrupo(secao.draggId))}
                                            </CCardBody>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>

                <CButton
                    className="btn-outline-primary btn-sm mb-3"
                    onClick={handlePlusSecao}
                    disabled={isView()}
                    title="Cria uma seção para agrupar itens por etapa, processo ou assunto"
                >
                    <CIcon name="cil-plus" className="c-icon mr-1"></CIcon> Adicionar seção
                </CButton>
            </DragDropContext>
        </Form>
    )
}

export default ChecklistItemForm;